<template>
  <div
    v-if="showSuccessState"
    class="text-center flex flex-col items-center justify-center gap-4 rounded border border-truegray-300 p-8 bg-white mt-8"
  >
    <div
      :class="[
        'flex items-center justify-center text-white p-4 rounded-full bg-elections-primary-600 text-secondary',
      ]"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        class="w-8 h-8"
      >
        <path
          fill-rule="evenodd"
          d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
          clip-rule="evenodd"
        />
      </svg>
    </div>
    <p :class="['text-center font-bold text-elections-primary-600']">
      Success!
    </p>
    <p class="text-center text-sm text-truegray-500">
      Thanks for your nomination. The team will be in touch to let you know if
      you are accepted as a candidate after review.
    </p>
  </div>
  <FormKit
    v-else
    v-model="form"
    type="form"
    :config="{
      classes: {
        label: 'text-elections-primary-600',
        legend: 'text-elections-primary-600 font-normal',
        help: 'text-tertiary',
        input: 'w-full',
        outer: '$remove:max-w-[20em]',
      },
    }"
    :submit-attrs="{
      inputClass: '$reset brand__button--tertiary',
    }"
    :actions="false"
    @submit="onSubmit"
  >
    <section v-show="isShowingNominationType" :class="CLASS_SECTION">
      <FormKit
        type="select"
        name="nominationType"
        label="I am nominating..."
        :options="OPTIONS_NOMINATION_TYPES"
        select-icon="caretDown"
        placeholder="Select an option"
        :label-class="LABEL_CLASS"
        outer-class="lg:w-2/5"
      />
    </section>

    <section v-show="isShowingNominatorDetails" :class="CLASS_SECTION">
      <p class="w-2/3">
        To nominate another person, fill out the downloaded form and email it to
        us at
        <a
          class="brand__link--tertiary"
          @click="() => copyToClipboard('election@electionpilot.nz', 'Email')"
          >election@electionpilot.nz</a
        >
        .
      </p>
    </section>

    <section v-show="isShowingCandidateDetails" :class="CLASS_SECTION">
      <!-- Before candidate details: render groups with position 'before_candidate_details' -->
      <template
        v-if="
          isCustomFieldElection &&
          selectedElection?.field_config?.custom_field_groups
        "
      >
        <template
          v-for="(group, groupKey) in selectedElection.field_config.custom_field_groups"
          :key="groupKey"
        >
          <template v-if="group.position === 'before_candidate_details'">
            <h5 class="text-elections-primary-600">{{ group.title }}</h5>
            <div :class="CLASS_GRID">
              <template
                v-for="(fieldConfig, fieldName) in group.fields"
                :key="fieldName"
              >
                <FormKit
                  :type="fieldName.includes('email') ? 'email' : 'text'"
                  :name="`c${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`"
                  :label="(fieldConfig.display_label || fieldConfig.label) + (fieldConfig.required ? ' *' : '')"
                  :validation="fieldName.includes('email') && fieldConfig.required ? 'required|email' : (fieldConfig.required ? 'required|contains_alphanumeric' : '')"
                  :validation-label="fieldConfig.display_label || fieldConfig.label"
                  :label-class="LABEL_CLASS"
                  :outer-class="fieldConfig.full_width ? 'col-span-2' : ''"
                />

                <!-- Confirm email after email fields -->
                <FormKit
                  v-if="fieldName.includes('email')"
                  type="email"
                  :name="`c${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}Confirm`"
                  :label="`Confirm ${fieldConfig.display_label || fieldConfig.label} *`"
                  validation="required|valid"
                  :validation-rules="{
                    valid: ({ value }) => {
                      return value === form[`c${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`]
                    },
                  }"
                  :validation-messages="{
                    valid: 'Emails must match.',
                  }"
                  :validation-label="`Confirm ${fieldConfig.display_label || fieldConfig.label}`"
                  :label-class="LABEL_CLASS"
                />
              </template>
            </div>
            <hr class="col-span-2 my-4 border-tertiary" />
          </template>
        </template>
      </template>

      <h5 class="text-elections-primary-600">Candidate (Nominee) Details</h5>
      <p class="italic text-sm text-truegray-500 mb-6">
        Please note: Your nomination form must be completed in one full sitting.
      </p>
      <div
        v-if="!selectedElection?.field_config?.hidden_fields?.includes('title')"
        :class="CLASS_GRID"
      >
        <FormKit
          type="text"
          name="cTitle"
          label="Title *"
          placeholder="Mr, Mrs, Ms, etc."
          validation="required|contains_alphanumeric"
          validation-label="Title"
          :label-class="LABEL_CLASS"
        />
      </div>
      <div :class="CLASS_GRID">
        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('first_name')"
          type="text"
          name="cFirstName"
          label="First name *"
          validation="required|contains_alphanumeric"
          validation-label="First name"
          :label-class="LABEL_CLASS"
        />

        <!-- Inline middle name for election types with candidate_extra group -->
        <template
          v-if="
            isCustomFieldElection &&
            selectedElection?.field_config?.custom_field_groups?.candidate_extra
          "
        >
          <FormKit
            v-for="(fieldConfig, fieldName) in selectedElection.field_config.custom_field_groups.candidate_extra.fields"
            :key="fieldName"
            :type="fieldConfig.type"
            :name="`c${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`"
            :label="(fieldConfig.display_label || fieldConfig.label) + (fieldConfig.required ? ' *' : '')"
            :validation="fieldConfig.required ? 'required|contains_alphanumeric' : ''"
            :validation-label="fieldConfig.display_label || fieldConfig.label"
            :label-class="LABEL_CLASS"
          />
        </template>

        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('last_name')"
          type="text"
          name="cLastName"
          label="Last name *"
          validation="required|contains_alphanumeric"
          validation-label="Last name"
          :label-class="LABEL_CLASS"
        />

        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('phone')"
          type="tel"
          name="cPhone"
          label="Phone number *"
          validation="required|number|length:3,20"
          validation-label="Phone number"
          :label-class="LABEL_CLASS"
        />

        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('email')"
          name="cEmail"
          label="Email address *"
          validation="required|valid"
          :validation-rules="{
            valid: ({ value }) => {
              const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u
              return emailPattern.test(value)
            },
          }"
          :validation-messages="{
            valid: `Please enter a valid email address.`,
          }"
          validation-label="Email address"
          :label-class="LABEL_CLASS"
        />

        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('confirm_email')"
          name="cConfirmEmail"
          label="Confirm Email address *"
          validation="required|valid"
          :validation-rules="{
            valid: ({ value }) => {
              return value === cEmail
            },
          }"
          :validation-messages="{
            valid: `Emails must match.`,
          }"
          validation-label="Confirm Email address"
          :label-class="LABEL_CLASS"
        />

        <!-- Date of Birth for specific election types (e.g., Type 6: Whakataki Marae) -->
        <template
          v-if="
            isCustomFieldElection &&
            selectedElection?.field_config?.custom_field_groups?.personal_details
          "
        >
          <FormKit
            v-for="(fieldConfig, fieldName) in selectedElection.field_config
              .custom_field_groups.personal_details.fields"
            :key="fieldName"
            :type="fieldConfig.type"
            :name="`c${
              fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
            }`"
            :label="(fieldConfig.display_label || fieldConfig.label) + (fieldConfig.required ? ' *' : '')"
            :validation="fieldConfig.required ? 'required' : ''"
            :validation-label="fieldConfig.display_label || fieldConfig.label"
            :label-class="LABEL_CLASS"
          />
        </template>

        <hr
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('address')"
          class="col-span-2 my-4 border-tertiary"
        />

        <h5
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('address')"
          class="col-span-2 text-elections-primary-600"
        >
          Address
        </h5>
        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('address')"
          type="text"
          name="cAddress1"
          label="Address Line 1 *"
          validation="required|contains_alphanumeric|length:2"
          validation-label="Address Line 1"
          :label-class="LABEL_CLASS"
        />
        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('address')"
          type="text"
          name="cAddress2"
          label="Address Line 2"
          validation="contains_alphanumeric|length:2"
          validation-label="Address Line 2"
          :label-class="LABEL_CLASS"
        />
        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('address')"
          type="text"
          name="cTown"
          label="Town/city *"
          validation="required|contains_alphanumeric|length:2"
          validation-label="Town/city"
          :label-class="LABEL_CLASS"
        />
        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('address')"
          type="text"
          name="cRegion"
          label="Region *"
          validation="required|contains_alphanumeric|length:2"
          validation-label="Region"
          :label-class="LABEL_CLASS"
        />
        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('address')"
          type="text"
          name="cPostcode"
          label="Postcode *"
          validation="required|number|length:4,4"
          validation-label="Postcode"
          :label-class="LABEL_CLASS"
        />

        <hr
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('image') || !selectedElection?.field_config?.hidden_fields?.includes('gender') || !selectedElection?.field_config?.hidden_fields?.includes('ethnicity')"
          class="col-span-2 my-4 border-tertiary"
        />

        <div class="col-span-1 flex flex-col gap-6">
          <div
            v-if="!selectedElection?.field_config?.hidden_fields?.includes('image')"
            class="w-full"
          >
            <div
              class="flex items-center justify-center md:justify-start flex-wrap md:flex-nowrap w-full"
            >
              <FormKit
                type="file"
                label="Profile image"
                name="cImage"
                accept=".jpg,.png,.jpeg"
                no-files-icon="fileImage"
                file-item-icon="fileImage"
                file-remove-icon="close"
                :help="`Ensure the focus of your image is central. Jpg, jpeg, png accepted under ${MAX_IMAGE_SIZE_MB} MB.`"
                outer-class="$remove:mb-4"
                :label-class="LABEL_CLASS"
                :validation-rules="{
                  fileSize: (node) => {
                    if (!node.value || !node.value.length) return true
                    const file = node.value[0]?.file
                    if (!file) return true
                    return file.size <= MAX_IMAGE_SIZE_BYTES
                  },
                }"
                :validation-messages="{
                  fileSize: `Image must be under ${MAX_IMAGE_SIZE_MB} MB`,
                }"
                validation="fileSize"
              />
              <ProfileImage
                :src="imageSrc"
                :show-placeholder="true"
                wrapper-class="my-4 mx-8 bg-white w-24 h-24 flex items-center justify-center rounded-full object-cover border-4 transition-all"
              />
            </div>
          </div>

          <FormKit
            v-if="!selectedElection?.field_config?.hidden_fields?.includes('gender')"
            type="select"
            name="cGender"
            label="Gender"
            :options="OPTIONS_GENDER"
            select-icon="caretDown"
            placeholder="Select gender"
            :label-class="LABEL_CLASS"
          />
        </div>

        <div
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('ethnicity')"
          class="relative"
        >
          <FormKit
            type="checkbox"
            name="cNationalities"
            label="Ethnicity"
            :options="OPTIONS_NATIONALITIES"
            help="Select as many as apply"
            decorator-icon="check"
            outer-class="md:mt-5 md:pl-8"
            :label-class="LABEL_CLASS"
          />
        </div>

        <div
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('statement')"
          class="col-span-2 flex flex-col"
        >
          <FormKit
            name="cBio"
            label="Statement"
            :validation-rules="{
              maxWords: (node) => {
                const wordCount = getCBioWordCount(node.value)
                return wordCount <= MAX_WORD_COUNT
              },
            }"
            :validation-messages="{
              maxWords: `Statement must not exceed ${MAX_WORD_COUNT} words.`,
            }"
            :help="`Maximum of ${MAX_WORD_COUNT} words`"
            validation="maxWords"
            validation-label="Statement"
            inner-class="$remove:px-3 $remove:py-2"
            outer-class="$remove:mb-4"
            :label-class="LABEL_CLASS"
          >
            <template #input>
              <Editor @update:content="updateEditorContent" />
            </template>
          </FormKit>
          <div class="text-xs text-truegray-500 mt-2">
            {{ cBioWordCount }} / {{ MAX_WORD_COUNT }} words
          </div>
        </div>

        <FormKit
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('prior_experience')"
          type="select"
          name="cPrior"
          :options="OPTIONS_PRIOR_EXPERIENCE"
          select-icon="caretDown"
          label="Prior experience"
          placeholder="Select prior experience"
          :label-class="LABEL_CLASS"
        />

        <!-- Custom field groups for this election type (rendered in candidate details section) -->
        <template
          v-if="
            isCustomFieldElection &&
            selectedElection?.field_config?.custom_field_groups
          "
        >
          <template
            v-for="(group, groupKey) in selectedElection.field_config
              .custom_field_groups"
            :key="groupKey"
          >
            <!-- Skip groups rendered elsewhere -->
            <template v-if="groupKey !== 'personal_details' && groupKey !== 'candidate_extra' && group.position !== 'before_candidate_details'">
            <hr class="col-span-2 my-4 border-tertiary" />
            <!-- <h5 class="col-span-2 text-elections-primary-600">
              {{ group.title }}
            </h5> -->

            <template
              v-for="(fieldConfig, fieldName) in group.fields"
              :key="fieldName"
            >
              <!-- Textarea fields (like whakapapa) -->
              <div
                v-if="fieldConfig.type === 'textarea'"
                class="col-span-2 flex flex-col"
              >
                <FormKit
                  :name="`c${
                    fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
                  }`"
                  :label="(fieldConfig.display_label || fieldConfig.label) + (fieldConfig.required ? ' *' : '')"
                  :help="fieldConfig.description"
                  :validation="fieldConfig.required ? 'required' : ''"
                  :validation-label="fieldConfig.display_label || fieldConfig.label"
                  inner-class="$remove:px-3 $remove:py-2"
                  outer-class="$remove:mb-4"
                  :label-class="LABEL_CLASS"
                >
                  <template #input>
                    <Editor
                      @update:content="
                        (content) =>
                          updateCustomFieldContent(fieldName, content)
                      "
                    />
                  </template>
                </FormKit>
              </div>

              <!-- Checkbox group fields (like iwi affiliations) -->
              <div
                v-else-if="fieldConfig.type === 'checkbox_group'"
                class="relative"
              >
                <FormKit
                  type="checkbox"
                  :name="`c${
                    fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
                  }`"
                  :label="(fieldConfig.display_label || fieldConfig.label) + (fieldConfig.required ? ' *' : '')"
                  :options="fieldConfig.options"
                  :help="
                    fieldConfig.description ||
                    `Select up to ${
                      fieldConfig.max_selections || 'multiple'
                    } options`
                  "
                  :validation="fieldConfig.required ? 'required' : ''"
                  :validation-label="fieldConfig.display_label || fieldConfig.label"
                  decorator-icon="check"
                  outer-class="md:mt-5 md:pl-8"
                  :label-class="LABEL_CLASS"
                />
              </div>

              <!-- Radio button fields (like yes/no questions) -->
              <div
                v-else-if="fieldConfig.type === 'radio'"
                class="relative col-span-2"
              >
                <FormKit
                  type="radio"
                  :name="`c${
                    fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
                  }`"
                  :label="(fieldConfig.display_label || fieldConfig.label) + (fieldConfig.required ? ' *' : '')"
                  :options="fieldConfig.options"
                  :help="fieldConfig.description"
                  :validation="fieldConfig.required ? 'required' : ''"
                  :validation-label="fieldConfig.display_label || fieldConfig.label"
                  outer-class="md:mt-5"
                  :label-class="LABEL_CLASS"
                />
              </div>
            </template>

            <hr class="col-span-2 my-4 border-tertiary" />
            </template>
          </template>
        </template>

        <!-- NZSBA additional nomination info and consent form -->
        <template v-if="isNZSBA">
          <div class="col-span-2">
            <p class="text-sm text-truegray-600 mb-4">
              If you wish to provide your profile photo<template v-if="selectedElection?.field_config?.consent_form_download_url">, candidate profile statement and/or consent form</template><template v-else> and/or candidate profile statement</template> separately, these can be sent by email to
              <a
                href="mailto:nominations@electionpilot.nz"
                class="brand__link--tertiary"
              >nominations@electionpilot.nz</a>, please include your name and the name of the election you are standing for in the email. All additional documents must be received before the close of nominations otherwise the nomination may be declared invalid.
            </p>
          </div>

          <template v-if="selectedElection?.field_config?.consent_form_download_url">
            <div class="col-span-2 flex items-center gap-4 flex-wrap">
              <a
                :href="selectedElection.field_config.consent_form_download_url"
                target="_blank"
                rel="noopener noreferrer"
                class="brand__button--trans-elections-primary inline-block"
              >
                Download Consent Form
              </a>
            </div>

            <div class="col-span-2">
              <FormKit
                type="file"
                label="Upload Consent Form"
                name="cConsentForm"
                accept=".pdf"
                no-files-icon="fileDoc"
                file-item-icon="fileDoc"
                file-remove-icon="close"
                help="Upload your completed consent form."
                :label-class="LABEL_CLASS"
              />
            </div>
          </template>

          <hr class="col-span-2 my-4 border-tertiary" />
        </template>

        <div
          v-if="!selectedElection?.field_config?.hidden_fields?.includes('terms')"
          class="relative col-span-2"
        >
          <!-- Custom consent fields (appear before eligibility terms) -->
          <template
            v-if="
              isCustomFieldElection &&
              selectedElection?.field_config?.custom_consent_fields
            "
          >
            <FormKit
              v-for="(fieldConfig, fieldName) in selectedElection.field_config
                .custom_consent_fields"
              :key="fieldName"
              type="checkbox"
              :name="`c${
                fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
              }`"
              :validation="fieldConfig.required ? 'accepted' : ''"
              :validation-label="fieldConfig.display_label || fieldConfig.label"
              decorator-icon="check"
              outer-class="$remove:mb-4 mb-2"
              :label-class="LABEL_CLASS"
            >
              <template #label>
                <div>{{ (fieldConfig.display_label || fieldConfig.label) + (fieldConfig.required ? ' *' : '') }}</div>
              </template>
            </FormKit>
          </template>

          <!-- Eligibility criteria notice for specific election types -->
          <p
            v-if="selectedElection?.type === 6"
            class="text-sm text-truegray-600 mb-4"
          >
            It is important that you have read the eligibility criteria to be a trustee. By checking the box below you are confirming that you have read the requirements and you are an eligible candidate to be a trustee.
          </p>

          <!-- Eligibility Terms checkbox -->
          <FormKit
            type="checkbox"
            name="cEligibilityTerms"
            decorator-icon="check"
            validation="accepted"
            validation-label="Eligibility Terms"
            outer-class="$remove:mb-4 mb-2"
            :label-class="LABEL_CLASS"
          >
            <template #label>
              <div class="flex space-x-1 flex-wrap">
                <span class="whitespace-nowrap">I have read and agree to the</span>
                <EligibilityTermsModal>
                  <template #trigger>
                    <a class="brand__link--tertiary whitespace-nowrap">
                      Eligibility Terms
                    </a>
                  </template>
                </EligibilityTermsModal>
                .
              </div>
            </template>
          </FormKit>

          <!-- Privacy Policy and Terms of Service checkbox -->
          <FormKit
            type="checkbox"
            name="cTerms"
            decorator-icon="check"
            validation="accepted"
            validation-label="Privacy Policy and Terms of Service"
            :outer-class="selectedElection?.field_config?.hidden_fields?.includes('consent') ? '$remove:mb-4' : '$remove:mb-4 mb-2'"
            :label-class="LABEL_CLASS"
          >
            <template #label>
              <div class="flex space-x-1 flex-wrap">
                <span class="whitespace-nowrap">I have read and agree to the</span>
                <ModalElectionPrivacy>
                  <template #trigger>
                    <a class="brand__link--tertiary whitespace-nowrap"
                      >Privacy Policy</a
                    >
                  </template>
                </ModalElectionPrivacy>
                <span class="whitespace-nowrap">and the</span>
                <ModalElectionTerms>
                  <template #trigger>
                    <a class="brand__link--tertiary whitespace-nowrap"
                      >Terms of Service</a
                    >
                  </template>
                </ModalElectionTerms>
                .
              </div>
            </template>
          </FormKit>

          <!-- Contact information sharing consent -->
          <FormKit
            v-if="!selectedElection?.field_config?.hidden_fields?.includes('consent')"
            type="checkbox"
            name="cConsent"
            decorator-icon="check"
            outer-class="$remove:mb-4"
            :label-class="LABEL_CLASS"
          >
            <template #label>
              <div>
                I agree for my contact information to be shared with
                <a
                  href="https://www.tewhakaroputanga.org.nz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="brand__link--tertiary"
                >
                  Te Whakarōputanga Kaitiaki Kura o Aotearoa
                </a>
                (NZ School Boards Association) for the provision of training and
                advice to school board members.
              </div>
            </template>
          </FormKit>
        </div>
      </div>
    </section>

    <div
      :class="[
        'flex gap-2 mt-6 justify-center items-center flex-col sm:flex-row',
      ]"
    >
      <FormKit
        v-show="currentStep !== STEPS[0]"
        type="button"
        label="Cancel"
        input-class="$reset brand__button--trans-elections-primary"
        outer-class="$remove:grow"
        :label-class="LABEL_CLASS"
        :disabled="isSubmitting"
        @click="prevStep"
      />

      <FormKit
        v-show="isLastStep"
        type="submit"
        label="Submit nomination"
        input-class="$reset brand__button--elections-primary"
        outer-class="$remove:grow"
        :disabled="isSubmitting"
      >
        <span v-if="isSubmitting" class="mr-1 w-5 h-5">
          <Vue3Lottie :animation-data="ANIMATION_LOADING.DARK" />
        </span>
        Submit nomination
      </FormKit>

      <FormKit
        v-show="!isLastStep && !isShowingNominatorDetails"
        type="button"
        label="Next"
        input-class="$reset brand__button--elections-primary"
        :disabled="isNextDisabled"
        @click="nextStep"
      />

      <FormKit
        v-show="
          isShowingNominatorDetails && selectedElection.nomination_paper_path
        "
        type="button"
        label="Download Form"
        input-class="$reset brand__button--elections-primary"
        @click="downloadNominationForm"
      />
    </div>
  </FormKit>
</template>

<script>
import { defineComponent, computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getNode } from '@formkit/core'
import Editor from './Editor.vue'
import ProfileImage from './ProfileImage.vue'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { Vue3Lottie } from 'vue3-lottie'
import { ANIMATION_LOADING } from '../../../constants.js'
import EligibilityTermsModal from './EligibilityTermsModal.vue'
import ModalElectionPrivacy from '../../../components/modal/ModalElectionPrivacy.vue'
import ModalElectionTerms from '../../../components/modal/ModalElectionTerms.vue'

export default defineComponent({
  name: 'FormNomination',

  components: {
    Vue3Lottie,
    Editor,
    ProfileImage,
    EligibilityTermsModal,
    ModalElectionPrivacy,
    ModalElectionTerms,
  },

  setup() {
    const store = useStore()

    const selectedElection = computed(
      () => store.getters['elections/getCurrentElection'],
    )

    // Election type helpers
    const isCustomFieldElection = computed(
      () => [6, 7, 8].includes(selectedElection.value?.type),
    )
    const isNZSBA = computed(
      () =>
        selectedElection.value?.field_config?.nomination_flow === 'nzsba',
    )

    // Store/constants
    const MAX_WORD_COUNT = computed(
      () => selectedElection.value?.field_config?.max_word_count || 400,
    )
    const MAX_IMAGE_SIZE_MB = 1
    const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024
    const OPTIONS_GENDER = computed(
      () => store.getters['elections/getOptionsGenderMappedForFormKit'],
    )

    const OPTIONS_NATIONALITIES = computed(
      () => store.getters['elections/getNationalitiesMappedForFormKit'],
    )

    const OPTIONS_PRIOR_EXPERIENCE = computed(
      () =>
        store.getters['elections/getOptionsPriorExperienceMappedForFormKit'],
    )

    const CLASS_SECTION = 'flex flex-col gap-2 mt-6 mb-4'
    const CLASS_GRID =
      'flex flex-col flex-wrap md:grid md:grid-cols-2 gap-8 w-full'
    const LABEL_CLASS = 'text-elections-primary-600 font-normal'

    const SELF_KEY = 'self'
    const OPTIONS_NOMINATION_TYPES = [
      {
        label: 'Myself',
        value: SELF_KEY,
      },
      {
        label: 'Someone else',
        value: 'other',
      },
    ]

    // Form/inputs
    const form = ref({
      nominationType: null,
      nEmail: null,
      nFullName: null,
      cTitle: null,
      cFirstName: null,
      cLastName: null,
      cImage: null,
      cGender: null,
      cNationalities: [],
      cEmail: null,
      cPhone: null,
      cBio: null,
      cPrior: null,
      cAddress1: null,
      cAddress2: null,
      cTown: null,
      cRegion: null,
      cPostcode: null,
      cConsentForm: null,
      cEligibilityTerms: false,
      cTerms: false,
      cConsent: false,
    })

    const updateEditorContent = (val) => {
      form.value.cBio = val
    }

    const updateCustomFieldContent = (fieldName, val) => {
      // Convert field name to form field name (e.g., whakapapa -> cWhakapapa)
      const formFieldName = `c${
        fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
      }`
      form.value[formFieldName] = val
    }

    // Validation requires re-calculating the wordcount inline for some reason;
    // using the computed prop inline alone doesn't react.
    const getCBioWordCount = (content) => {
      // Remove HTML tags to get the raw content (entities) as a
      // single string, with the entities delimited by whitespace
      const strippedContent = content?.replace(/<[^>]*>?/gm, ' ')

      return (
        strippedContent
          ?.trim()
          // Split delimited entities into an array of substrings
          ?.split(/\s+/)
          // Filter out strings that are actually standalone characters,
          // as they don't count for the word limit!
          ?.filter((v) => v && !/^[\p{P}\p{S}]+$/u.test(v))?.length || 0
      )
    }

    const cBioWordCount = computed(() => {
      return getCBioWordCount(form.value.cBio)
    })

    const cEmail = computed(() => form.value?.cEmail)

    // Profile image
    const imageInput = computed(() => form.value?.cImage)
    const imageData = computed(() => imageInput.value?.[0] || null)
    const imageFile = computed(() => imageData.value?.file || null)
    const imageSrc = computed(() => {
      // If a new file is selected, preview that.
      if (imageFile.value) {
        return URL.createObjectURL(imageFile.value)
      }

      // Otherwise preview the existing image (or nothing if none exists).
      return typeof imageInput.value === 'string' ? imageInput.value : null
    })

    // Google recaptcha
    const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()
    const runRecaptcha = async () => {
      // Wait until recaptcha has been loaded
      await recaptchaLoaded()

      // Execute reCAPTCHA with form action
      const action = 'candidate_signup'
      const token = await executeRecaptcha(action)
      return { token, action }
    }

    // Steps
    const isNominatingSelf = computed(
      () => form.value?.nominationType === SELF_KEY,
    )
    const STEP_KEYS = ['nominationType', 'nominatorDetails', 'candidateDetails']

    const STEPS = computed(() => {
      // For Whakataki Marae (type 6) and NZSBA elections, single-page form
      if (selectedElection.value?.type === 6 || isNZSBA.value) {
        return [STEP_KEYS[2]] // Only show candidate details
      }

      const steps = [STEP_KEYS[0], STEP_KEYS[2]]
      if (!isNominatingSelf.value) {
        steps.splice(1, 0, STEP_KEYS[1])
      }
      return steps
    })

    const currentStep = ref(STEPS.value[0])
    const nextButtonText = computed(() =>
      isLastStep.value ? 'Submit nomination' : 'Next',
    )

    const isShowingNominationType = computed(
      () => currentStep.value === STEP_KEYS[0],
    )

    const isShowingNominatorDetails = computed(
      () => currentStep.value === STEP_KEYS[1],
    )

    const isShowingCandidateDetails = computed(
      () => currentStep.value === STEP_KEYS[2],
    )

    const isLastStep = computed(() => isShowingCandidateDetails.value)

    const isNextDisabled = computed(() => {
      if (currentStep.value === STEP_KEYS[0]) {
        return isSubmitting.value || !form.value.nominationType
      }

      if (currentStep.value === STEP_KEYS[1]) {
        const nFullNameValid = getNode('nFullName')?.context?.state?.valid
        const nEmailValid = getNode('nEmail')?.context?.state?.valid
        return isSubmitting.value || !nFullNameValid || !nEmailValid
      }

      return isSubmitting.value
    })

    const nextStep = () => {
      const currentIndex = STEPS.value.indexOf(currentStep.value)
      if (currentIndex < STEPS.value.length - 1) {
        currentStep.value = STEPS.value[currentIndex + 1]
      }
    }

    const prevStep = () => {
      const currentIndex = STEPS.value.indexOf(currentStep.value)
      if (currentIndex > 0) {
        currentStep.value = STEPS.value[currentIndex - 1]
      }
    }

    const downloadNominationForm = () => {
      const pdfLoc = selectedElection.value.nomination_paper_path || null

      if (pdfLoc) {
        window.location.href = pdfLoc
      }
    }

    const copyToClipboard = (item, type) => {
      navigator.clipboard?.writeText(item)
      store.dispatch('global/dispatchUpdateAlertToast', {
        status: 'success',
        message: `${type} copied to clipboard`,
      })
    }

    // State
    const showSuccessState = ref(false)
    const isSubmitting = ref(false)

    // Watch for election type changes to auto-set special election flows
    watch(selectedElection, (newElection) => {
      if (newElection?.type === 6 || newElection?.field_config?.nomination_flow === 'nzsba') {
        // Single-page form: skip nomination type selection
        form.value.nominationType = SELF_KEY
        currentStep.value = STEPS.value[0]
      }
    }, { immediate: true })

    const onSubmit = async () => {
      isSubmitting.value = true
      const recaptchaData = await runRecaptcha()

      const payload = new FormData()
      Object.keys(form.value).forEach((key) => {
        if (key === 'cImage' && imageFile.value) {
          payload.append('cImage', imageFile.value)
        } else if (key === 'cConsentForm' && form.value[key]?.[0]?.file) {
          payload.append('cConsentForm', form.value[key][0].file)
        } else if (form.value[key] !== null) {
          // Check if this is a dynamic custom field by looking for it in the election config
          const isCustomField = selectedElection.value?.field_config?.custom_field_groups &&
            Object.values(selectedElection.value.field_config.custom_field_groups)
              .some(group => Object.keys(group.fields || {})
                .some(fieldName => key === `c${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`))

          // Handle arrays for custom fields only
          if (isCustomField && Array.isArray(form.value[key])) {
            payload.append(key, JSON.stringify(form.value[key]))
          } else {
            payload.append(key, form.value[key])
          }
        }
      })

      payload.append('recaptchaData', JSON.stringify(recaptchaData))

      const success = await store.dispatch(
        'elections/dispatchUpsertCandidate',
        payload,
      )

      if (success) {
        showSuccessState.value = true
      }

      isSubmitting.value = false
    }

    return {
      ANIMATION_LOADING,
      CLASS_SECTION,
      CLASS_GRID,
      LABEL_CLASS,
      MAX_WORD_COUNT,
      MAX_IMAGE_SIZE_MB,
      MAX_IMAGE_SIZE_BYTES,
      OPTIONS_GENDER,
      OPTIONS_NATIONALITIES,
      OPTIONS_PRIOR_EXPERIENCE,
      OPTIONS_NOMINATION_TYPES,
      STEPS,
      selectedElection,
      currentStep,
      isNextDisabled,
      isLastStep,
      nextButtonText,
      nextStep,
      prevStep,
      downloadNominationForm,
      copyToClipboard,

      form,
      cEmail,
      getCBioWordCount,
      cBioWordCount,
      imageSrc,
      isNominatingSelf,
      isCustomFieldElection,
      isNZSBA,
      isShowingNominationType,
      isShowingNominatorDetails,
      isShowingCandidateDetails,

      updateEditorContent,
      updateCustomFieldContent,
      showSuccessState,
      isSubmitting,
      onSubmit,
    }
  },
})
</script>

<style>
.grecaptcha-badge {
  z-index: 100;
}
</style>
